import { showAlertModal } from "@/utils/utils";

/**
 * @param {Object} product
 * @returns {string|null}
 */
export function resolveProductCode(product) {
    const candidates = [product?.product_code, product?.part_no];
    for (const candidate of candidates) {
        if (candidate == null) {
            continue;
        }
        const normalized = String(candidate).trim();
        if (normalized) {
            return normalized;
        }
    }
    return null;
}

function formatMissingProductsLabel(product) {
    const code = resolveProductCode(product) || product?.product_code;
    if (!code) {
        return "";
    }
    const name = product.product_name || product.name;
    return name && name !== code ? `${code} – ${name}` : code;
}

function normalizeMissingProductsForAlert(missingProducts) {
    const seen = new Set();
    const normalized = [];
    for (const product of missingProducts || []) {
        const code = resolveProductCode(product);
        if (!code || seen.has(code)) {
            continue;
        }
        const label = formatMissingProductsLabel({
            ...product,
            product_code: code,
        });
        if (!label?.trim()) {
            continue;
        }
        seen.add(code);
        normalized.push({
            product_code: code,
            name: product.product_name || product.name || code,
        });
    }
    return normalized;
}

/**
 * @param {Array<{product_code: string, name?: string}>} missingProducts
 * @returns {{ copyableText: string, isProductList: boolean, missingProducts: Array }}
 */
export function buildProductListErrorDetails(missingProducts) {
    const normalized = normalizeMissingProductsForAlert(missingProducts);
    return {
        copyableText: normalized
            .map(product => formatMissingProductsLabel(product))
            .filter(Boolean)
            .join("\n"),
        isProductList: true,
        missingProducts: normalized,
    };
}

export function buildMissingCalculationProductsUploadMessage() {
    return (
        "Atenție! Unele produse necesare pentru calcule și formule lipsesc din lista de prețuri încărcată. " +
        "Ofertele care folosesc „Calculează produse” pot avea rezultate incomplete. " +
        "Verificați următoarele produse:"
    );
}

/**
 * @param {Array} productEntries
 * @param {Array} priceListProducts
 * @returns {Array<{product_code: string, name: string}>}
 */
export function findProductsMissingFromPriceList(productEntries, priceListProducts) {
    const priceListCodes = new Set(
        (priceListProducts || [])
            .map(product => resolveProductCode(product))
            .filter(Boolean)
    );
    const seen = new Set();
    const missing = [];
    for (const product of productEntries || []) {
        const code = resolveProductCode(product);
        if (!code || seen.has(code) || priceListCodes.has(code)) {
            continue;
        }
        seen.add(code);
        missing.push({
            product_code: code,
            name: product.product_name || product.name || code,
        });
    }
    return missing;
}

function catalogEntriesFromProducts(catalogs) {
    const byCode = new Map();
    for (const catalog of catalogs) {
        for (const product of catalog || []) {
            const code = resolveProductCode(product);
            if (!code || byCode.has(code)) {
                continue;
            }
            byCode.set(code, {
                product_code: code,
                name: product.name || product.product_name || code,
            });
        }
    }
    return Array.from(byCode.values());
}

/**
 * Product codes from offer catalogs that use „Calculează produse”.
 * Catalogs are loaded on demand to avoid bloating the price-list page bundle.
 * @param {string} priceListType
 * @returns {Promise<Array<{product_code: string, name: string}>>}
 */
export async function loadCalculationRequiredProducts(priceListType) {
    if (priceListType !== "general") {
        return [];
    }

    const [
        { ceilingProducts },
        { ipa_products },
        { products: vmcProducts },
        { pdcProducts },
        { products: vacuumProducts },
        { plumbingProducts },
    ] = await Promise.all([
        import("@/CEILING/ceilingProducts.js"),
        import("@/IPA/IPAProducts.js"),
        import("@/VMC/vmcProducts.js"),
        import("@/PDC/pdcProducts.js"),
        import("@/Vacuum/VacuumProducts.js"),
        import("@/PLUMBING/plumbingProducts.js"),
    ]);

    return catalogEntriesFromProducts([
        ceilingProducts,
        ipa_products,
        vmcProducts,
        pdcProducts,
        vacuumProducts,
        plumbingProducts,
    ]);
}

/**
 * @param {string} priceListType
 * @param {Array} priceListProducts
 * @returns {Promise<Array<{product_code: string, name: string}>>}
 */
export async function findCalculationProductsMissingFromUploadedPriceList(
    priceListType,
    priceListProducts
) {
    const required = await loadCalculationRequiredProducts(priceListType);
    return findProductsMissingFromPriceList(required, priceListProducts);
}

/**
 * @param {Object} store - Vuex store
 * @param {Array<{product_code: string, name?: string}>} missingProducts
 */
export function showMissingCalculationProductsUploadAlert(store, missingProducts) {
    const errorDetails = buildProductListErrorDetails(missingProducts);
    if (!errorDetails.copyableText?.trim()) {
        return;
    }
    showAlertModal(
        store,
        buildMissingCalculationProductsUploadMessage(),
        "warning",
        15000,
        errorDetails
    );
}
